import React, { useState } from 'react';
import { Cpu, Clock } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { SignalDetail } from './SignalDetail';

interface Prediction {
  id: number;
  topic: string;
  prediction: string;
  time_horizon: string;
  confidence_score: number | null;
  llm_provider: string;
  created_at: string;
  supporting_signals?: number[];
}

const HORIZON_TONE: Record<string, string> = {
  '1_month': 'success',
  '3_months': 'info',
  '6_months': 'warning',
  '1_year': 'danger',
};

const HORIZON_LABEL: Record<string, string> = {
  '1_month': '1 month',
  '3_months': '3 months',
  '6_months': '6 months',
  '1_year': '1 year',
};

function ConfidenceBar({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color = pct >= 70 ? 'var(--color-success)' : pct >= 45 ? 'var(--color-warning)' : 'var(--color-danger)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <div style={{ flex: 1, height: 4, background: 'var(--color-elevated)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 'var(--radius-full)', transition: 'width 0.4s var(--easing-standard)' }} />
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)', minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

export function PredictionBoard() {
  const [horizon, setHorizon] = useState('');
  const [sigId, setSigId] = useState<number | null>(null);
  const params = horizon ? `?time_horizon=${horizon}` : '';
  const { data, loading } = useApi<{ predictions: Prediction[] }>(`/api/predictions${params}`, [horizon]);

  const horizons = ['', '1_month', '3_months', '6_months', '1_year'];

  return (
    <div className="stack" style={{ gap: 'var(--space-8)' }}>
      {/* Header */}
      <div>
        <span className="eyebrow">Forward Intelligence</span>
        <h1 className="t-headline-md" style={{ marginTop: 'var(--space-2)' }}>Prediction Board</h1>
      </div>

      {/* Horizon filter */}
      <div className="tabs">
        {horizons.map((h) => (
          <button
            key={h || 'all'}
            className={`tab${horizon === h ? ' is-active' : ''}`}
            onClick={() => setHorizon(h)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            {h ? (
              <>
                <Clock size={12} strokeWidth={1.75} />
                {HORIZON_LABEL[h]}
              </>
            ) : 'All'}
          </button>
        ))}
      </div>

      {loading && (
        <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>Loading predictions…</p>
      )}

      {data?.predictions.length === 0 && (
        <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>No predictions yet. Run the analysis pipeline to generate forecasts.</p>
      )}

      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        {data?.predictions.map((p) => {
          const tone = HORIZON_TONE[p.time_horizon] || 'neutral';
          return (
            <div key={p.id} className="card card-accent" data-accent={tone}>
              <div className="row-between">
                <div className="card-title">{p.topic}</div>
                <div className="row" style={{ gap: 'var(--space-2)' }}>
                  <span className="chip" data-tone={tone} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={10} strokeWidth={1.75} />
                    {HORIZON_LABEL[p.time_horizon] || p.time_horizon}
                  </span>
                </div>
              </div>

              <p className="t-body-sm" style={{ color: 'var(--color-text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {p.prediction}
              </p>

              {p.confidence_score != null && (
                <div>
                  <span className="t-label-sm" style={{ marginBottom: 'var(--space-2)', display: 'block' }}>Confidence</span>
                  <ConfidenceBar score={p.confidence_score} />
                </div>
              )}

              {p.supporting_signals && p.supporting_signals.length > 0 && (
                <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <span className="t-label-sm" style={{ alignSelf: 'center' }}>
                    Based on {p.supporting_signals.length}:
                  </span>
                  {p.supporting_signals.map((id) => (
                    <button
                      key={id}
                      className="chip"
                      data-tone="info"
                      onClick={() => setSigId(id)}
                      style={{ cursor: 'pointer' }}
                    >
                      Signal #{id}
                    </button>
                  ))}
                </div>
              )}

              <div className="row-between" style={{ marginTop: 'var(--space-1)' }}>
                <span className="chip" data-tone="neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Cpu size={11} strokeWidth={1.75} />
                  {p.llm_provider}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  {new Date(p.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {sigId !== null && <SignalDetail signalId={sigId} onClose={() => setSigId(null)} />}
    </div>
  );
}
